import { useQuery } from '@tanstack/react-query';
import { getChatHistory } from '../../../../api/document-rag/document-rag.api';
import type { GetChatHistoryResponse } from '../../../../api/document-rag/document-rag.api.types';
import { useAPIErrorHandler } from '../../../../components/hooks/useAPIErrorHandler';
import { getData } from '../../../../utils/functional/fp';
import type { ChatMessage } from '../_contexts/DocumentRagContext';
import { transformChatHistoryResponse } from './utils/chat-history-transform';

/**
 * Parameters for the useGetChatHistory hook
 */
export interface UseGetChatHistoryParams {
  /** Submission ID */
  submissionId: string;
  /** Whether the query should be enabled (optional, defaults to true when submissionId is present) */
  enabled?: boolean;
}

/**
 * Return type for the useGetChatHistory hook
 */
export interface UseGetChatHistoryReturn {
  /** Transformed chat history messages */
  messages: ChatMessage[];
  /** Raw API response */
  apiResponse: GetChatHistoryResponse | undefined;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query encountered an error */
  isError: boolean;
  /** Conversation ID from history (if available) */
  conversationId: string | null;
  /** Function to refetch the chat history */
  refetch: () => void;
}

/**
 * React Query hook for retrieving chat history for a submission
 *
 * @example
 * ```typescript
 * const { messages, isLoading, conversationId } = useGetChatHistory({
 *   submissionId: 'sub-456'
 * });
 * ```
 */
export function useGetChatHistory({
  submissionId,
  enabled,
}: UseGetChatHistoryParams): UseGetChatHistoryReturn {
  const handleAPIError = useAPIErrorHandler();

  const isEnabled = enabled !== undefined ? enabled : Boolean(submissionId);

  const query = useQuery<GetChatHistoryResponse>({
    queryKey: ['rag-chat-history', { submissionId }],
    queryFn: () =>
      getChatHistory({
        params: {
          submissionId,
        },
      }).then(getData),
    onError: handleAPIError(
      'Unable to retrieve chat history. Please try again.'
    ),
    enabled: isEnabled,
    staleTime: 0, // Always consider data stale - refetch when enabled
    refetchOnMount: 'always', // Always refetch when component mounts (modal opens)
  });

  // Transform chat history from API format to ChatMessage format
  // Assistant messages have content as JSON string that needs parsing
  const messages: ChatMessage[] = query.data
    ? transformChatHistoryResponse(query.data)
    : [];

  // Extract conversationId from response
  const conversationId = query.data?.conversationId || null;

  return {
    messages,
    apiResponse: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    conversationId,
    refetch: query.refetch,
  };
}
